"use client";

import * as React from "react";
import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  User,
  Search,
  MessageCircle,
  Award,
  Gift,
  HelpCircle,
  BookOpen,
  Lightbulb,
  LogOut
} from "lucide-react";
import { cn } from "../../lib/utils";
import { ProfileAvatar } from "./profile-avatar";

export interface ProfileDropdownProps {
  username: string;
  displayName?: string;
  avatarUrl?: string;
  profileUrl?: string;
  onAccountClick?: () => void;
  onSeoClick?: () => void;
  onWhatsAppClick?: () => void;
  onLeaderboardClick?: () => void;
  onReferEarnClick?: () => void;
  onAskQuestionClick?: () => void;
  onHelpTopicsClick?: () => void;
  onFeedbackClick?: () => void;
  onLogoutClick?: () => void;
  onNotificationsClick?: () => void;
  hasUnreadNotifications?: boolean;
  labels?: {
    account?: string;
    seo?: string;
    whatsapp?: string;
    leaderboard?: string;
    referEarn?: string;
    askQuestion?: string;
    helpTopics?: string;
    shareFeedback?: string;
    logout?: string;
    free?: string;
  };
}

export function ProfileDropdown({
  username,
  displayName,
  avatarUrl,
  profileUrl,
  onAccountClick,
  onSeoClick,
  onWhatsAppClick,
  onLeaderboardClick,
  onReferEarnClick,
  onAskQuestionClick,
  onHelpTopicsClick,
  onFeedbackClick,
  onLogoutClick,
  onNotificationsClick,
  hasUnreadNotifications = false,
  labels,
}: ProfileDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const profileName = displayName || username || "Creator";
  const defaultLabels = {
    account: "Account",
    seo: "SEO",
    whatsapp: "WhatsApp updates",
    leaderboard: "Leaderboard",
    referEarn: "Refer & Earn",
    askQuestion: "Ask question",
    helpTopics: "Help Topics",
    shareFeedback: "Share Feedback",
    logout: "Logout",
    ...labels,
  };

  const renderItem = ({
    label,
    icon: IconComponent,
    onClick,
    isDanger = false,
  }: {
    label: string;
    icon: React.ComponentType<{ className?: string }> | React.ReactNode;
    onClick?: () => void;
    isDanger?: boolean;
  }) => {
    return (
      <button
        type="button"
        onClick={() => {
          onClick?.();
          setIsOpen(false);
        }}
        className={cn(
          "flex w-full items-center gap-3.5 rounded-xl px-3.5 py-2 text-left text-[14px] font-medium transition-all duration-150 ease-out",
          isDanger
            ? "text-[#D92D20] hover:bg-[#FEF3F2]"
            : "text-[#344054] hover:bg-[#F9FAFB] hover:text-[#101828]"
        )}
      >
        <span className="flex h-5 w-5 shrink-0 items-center justify-center">
          {React.isValidElement(IconComponent) ? (
            IconComponent
          ) : IconComponent ? (
            // @ts-expect-error IconComponent is a component type
            <IconComponent className="h-[18px] w-[18px]" />
          ) : null}
        </span>
        <span className="truncate">{label}</span>
      </button>
    );
  };

  return (
    <div className="relative flex items-center px-2" ref={containerRef}>
      {/* Profile Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex min-w-0 items-center gap-2.5 text-left hover:opacity-85 focus:outline-none"
      >
        <ProfileAvatar
          src={avatarUrl}
          alt={profileName}
          className="h-8 w-8 rounded-full border border-black/10"
          fallbackClassName="bg-gray-100"
          logoClassName="h-[56%] w-[56%]"
        />
        <span className="truncate text-[15px] font-semibold text-[#101828]">
          {profileName}
        </span>
        <ChevronDown
          className={cn(
            "h-4 w-4 text-[#667085] transition-transform duration-200 ease-out",
            isOpen && "rotate-180"
          )}
        />
      </button>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.96 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className="absolute left-2 top-11 z-[10000] w-72 rounded-2xl border border-[#EAECF0]/80 bg-white p-2 shadow-[0_12px_36px_rgba(0,0,0,0.12)] focus:outline-none"
          >
            {/* Header section with profile details */}
            <div className="flex items-center gap-3 p-3">
              <ProfileAvatar
                src={avatarUrl}
                alt={profileName}
                className="h-12 w-12 rounded-full border border-black/10"
                fallbackClassName="bg-gray-100"
                logoClassName="h-[54%] w-[54%]"
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-[#101828]">
                  {profileName}
                </p>
                {profileUrl && (
                  <p className="truncate text-xs text-[#667085] mt-0.5">
                    {profileUrl}
                  </p>
                )}
              </div>
            </div>

            <div className="my-1 border-t border-[#EAECF0]" />

            {/* Section 1: Core Account Actions */}
            <div className="space-y-0.5">
              {renderItem({
                label: defaultLabels.account,
                icon: User,
                onClick: onAccountClick,
              })}
              {renderItem({
                label: defaultLabels.seo,
                icon: Search,
                onClick: onSeoClick,
              })}
              {renderItem({
                label: defaultLabels.whatsapp,
                icon: MessageCircle,
                onClick: onWhatsAppClick,
              })}
            </div>

            <div className="my-1 border-t border-[#EAECF0]" />

            {/* Section 2: Support & Feedback */}
            <div className="space-y-0.5">
              {renderItem({
                label: defaultLabels.askQuestion,
                icon: HelpCircle,
                onClick: onAskQuestionClick,
              })}
              {renderItem({
                label: defaultLabels.helpTopics,
                icon: BookOpen,
                onClick: onHelpTopicsClick,
              })}
              {renderItem({
                label: defaultLabels.shareFeedback,
                icon: Lightbulb,
                onClick: onFeedbackClick,
              })}
            </div>

            <div className="my-1 border-t border-[#EAECF0]" />

            {/* Section 3: Authentication */}
            <div className="space-y-0.5">
              {renderItem({
                label: defaultLabels.logout,
                icon: LogOut,
                onClick: onLogoutClick,
                isDanger: true,
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
